import React, { useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";

const getMainColor = () => "var(--widget-main-color, #ee754c)";

const Container = styled.div`
  width: 100%;
  height: ${({ $widgetMode }) => ($widgetMode ? "100%" : "420px")};
  max-height: 100%;
  min-height: ${({ $widgetMode }) => ($widgetMode ? "0" : "260px")};
  margin: 0 auto;
  padding: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(4px, 1.2vw, 10px)" : "20px"};
  box-sizing: border-box;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
`;

const Title = styled.span`
  justify-self: center;
  width: min(100%, 260px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  border-bottom: 1px solid
    ${({ $widgetDark }) =>
      $widgetDark ? "rgba(246, 241, 232, 0.28)" : "#99b0ad"};

  padding-bottom: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(3px, 1vw, 7px)" : "10px"};
  margin-bottom: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(4px, 1vw, 7px)" : "10px"};

  color: ${({ $widgetDark }) => ($widgetDark ? "#f6f1e8" : "#99b0ad")};
  font-size: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(11px, 2.6vw, 14px)" : "15px"};
  font-weight: 700;
  line-height: 1.1;
  text-align: center;

  svg {
    color: ${({ $widgetDark }) => ($widgetDark ? "#f6f1e8" : "#99b0ad")};
    font-size: ${({ $widgetMode }) =>
      $widgetMode ? "clamp(11px, 2.4vw, 14px)" : "14px"};
  }
`;

const Grid = styled.div`
  min-height: 0;
  width: 100%;
  padding: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(3px, 0.9vw, 7px)" : "10px"};
  box-sizing: border-box;

  display: grid;
  grid-template-columns: ${({ $widgetMode }) =>
    $widgetMode
      ? "repeat(4, minmax(0, 1fr))"
      : "repeat(auto-fill, minmax(70px, 1fr))"};

  grid-auto-rows: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(34px, 10vw, 46px)" : "50px"};

  gap: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(5px, 1.4vw, 9px)" : "15px"};

  align-content: start;

  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  scrollbar-color: ${getMainColor()} transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${getMainColor()};
    border-radius: 999px;
  }
`;

const Box = styled.button`
  width: 100%;
  height: 100%;
  min-width: 0;

  border: 1px solid
    ${({ $selected, $widgetDark }) => {
      if ($selected) return getMainColor();
      return $widgetDark
        ? "rgba(246, 241, 232, 0.28)"
        : "rgba(153, 176, 173, 0.9)";
    }};

  border-radius: ${({ $widgetMode }) => ($widgetMode ? "8px" : "10px")};

  background-color: ${({ disabled, $selected, $widgetDark }) => {
    if (disabled) {
      return $widgetDark
        ? "rgba(255, 255, 255, 0.06)"
        : "rgba(240, 240, 240, 0.95)";
    }

    if ($selected) return getMainColor();

    return $widgetDark ? "rgba(255, 255, 255, 0.06)" : "#ffffff";
  }};

  color: ${({ disabled, $selected, $widgetDark }) => {
    if (disabled) {
      return $widgetDark
        ? "rgba(255, 255, 255, 0.35)"
        : "rgba(153, 153, 153, 0.95)";
    }

    if ($selected) return "#ffffff";

    return $widgetDark ? "#ffffff" : "#99b0ad";
  }};

  font-size: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(0.82rem, 3vw, 1rem)" : "1.2em"};
  font-weight: 800;
  line-height: 1;

  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;
  box-sizing: border-box;

  transition:
    transform 0.15s ease,
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    transform: ${(props) => (props.disabled ? "none" : "scale(1.04)")};
    border-color: ${({ disabled }) => (disabled ? undefined : getMainColor())};
    background-color: ${({ disabled, $selected, $widgetDark }) => {
      if (disabled) {
        return $widgetDark
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(240, 240, 240, 0.95)";
      }

      if ($selected) return getMainColor();

      return $widgetDark
        ? "rgba(255, 255, 255, 0.11)"
        : "rgba(153, 176, 173, 0.12)";
    }};
  }
`;

const AddButtonWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(5px, 1vw, 8px)" : "20px"};
  box-sizing: border-box;
`;

const AddButton = styled.button`
  min-height: ${({ $widgetMode }) => ($widgetMode ? "28px" : "40px")};
  padding: ${({ $widgetMode }) => ($widgetMode ? "5px 12px" : "10px 20px")};

  background-color: ${(props) =>
    props.disabled ? "rgba(153, 153, 153, 0.65)" : getMainColor()};
  color: white;

  border: none;
  border-radius: ${({ $widgetMode }) => ($widgetMode ? "8px" : "10px")};

  font-size: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(0.74rem, 2.4vw, 0.86rem)" : "1em"};
  font-weight: 700;

  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  white-space: nowrap;

  &:hover {
    filter: ${(props) => (props.disabled ? "none" : "brightness(0.96)")};
  }
`;

const NumberOfPeopleSelector = ({
  numPeople,
  setNumPeople,
  from_restaurant,
  limit_people_reservation,
  widgetMode = false,
  widgetDark = false,
}) => {
  const [array, setArray] = useState(16);

  const limitPeopleReservation = Number(limit_people_reservation) || 0;
  const hasLimit = limitPeopleReservation > 0;

  const handleAddPeople = () => {
    if (from_restaurant || !hasLimit || array < limitPeopleReservation) {
      setArray((prev) => prev + 16);
    }
  };

  const handleBoxClick = (number) => {
    if (from_restaurant || !hasLimit || number <= limitPeopleReservation) {
      setNumPeople(number);
    }
  };

  const isAddDisabled =
    !from_restaurant && hasLimit && array >= limitPeopleReservation;

  return (
    <Container $widgetMode={widgetMode}>
      <Title $widgetMode={widgetMode} $widgetDark={widgetDark}>
        <FontAwesomeIcon icon={faUser} />
        Número de pessoas
      </Title>

      <Grid $widgetMode={widgetMode}>
        {Array.from({ length: array }, (_, index) => {
          const number = index + 1;
          const disabled =
            !from_restaurant && hasLimit && number > limitPeopleReservation;

          return (
            <Box
              key={number}
              type="button"
              $widgetMode={widgetMode}
              $widgetDark={widgetDark}
              $selected={Number(numPeople) === number}
              onClick={() => handleBoxClick(number)}
              disabled={disabled}
            >
              {number}
            </Box>
          );
        })}
      </Grid>

      <AddButtonWrap $widgetMode={widgetMode}>
        <AddButton
          type="button"
          $widgetMode={widgetMode}
          onClick={handleAddPeople}
          disabled={isAddDisabled}
        >
          + Pessoas
        </AddButton>
      </AddButtonWrap>
    </Container>
  );
};

export default NumberOfPeopleSelector;
